import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Search, FileText, Image as ImageIcon, 
  Type, AlignLeft, List, ChevronUp, ChevronDown, GripVertical, Save,
  ArrowLeft, X, BookOpen, Download, Instagram, Copy, Check, Heart, TrendingUp, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { positivizeString, scanForNegativity } from '../../utils/positivize';

// Convert raw HTML in a block paragraph to plain text for editing.
// Keeps line-breaks for <br>, </p>, </h1-6>, </li>. Strips all other tags.
const stripHtmlForEdit = (raw = '') => {
  if (!raw) return '';
  // Only strip if it actually looks like HTML; plain text stays untouched
  if (!/<[a-z][^>]*>/i.test(raw)) return raw;
  return raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h[1-6]|li|div)\s*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Baustein-Typen
const BLOCK_TYPES = [
  { type: 'heading', icon: Type, label: 'Überschrift', color: '#FFF8F0', textColor: '#E89860' },
  { type: 'paragraph', icon: AlignLeft, label: 'Textabschnitt', color: '#F0F8FF', textColor: '#60A0D0' },
  { type: 'image', icon: ImageIcon, label: 'Bild', color: '#F0FFF0', textColor: '#60B060' },
  { type: 'list', icon: List, label: 'Aufzählung', color: '#F8F0FF', textColor: '#A070D0' },
];

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Editor State
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postTitle, setPostTitle] = useState('');
  const [postExcerpt, setPostExcerpt] = useState('');
  const [postImage, setPostImage] = useState('');
  const [postCategory, setPostCategory] = useState('');
  const [postTags, setPostTags] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [blocks, setBlocks] = useState([]);

  // PDF preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [showPdfDialog, setShowPdfDialog] = useState(false);

  // Instagram teaser state
  const [igPost, setIgPost] = useState(null);
  const [showIgDialog, setShowIgDialog] = useState(false);
  const [copiedCaption, setCopiedCaption] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/blog/admin/posts');
      setPosts(response.data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Fehler beim Laden der Beiträge');
    } finally {
      setLoading(false);
    }
  };

  // Content zu Blöcken konvertieren
  const contentToBlocks = (content) => {
    if (!content) return [];
    const lines = content.split('\n\n');
    return lines.map((line, index) => {
      if (line.startsWith('# ')) {
        return { id: `block-${index}`, type: 'heading', content: line.substring(2) };
      } else if (line.startsWith('- ')) {
        return { id: `block-${index}`, type: 'list', content: line };
      } else if (line.startsWith('![')) {
        const match = line.match(/!\[.*?\]\((.*?)\)/);
        return { id: `block-${index}`, type: 'image', content: match ? match[1] : '' };
      } else {
        return { id: `block-${index}`, type: 'paragraph', content: line };
      }
    }).filter(b => b.content.trim());
  };

  // Blöcke zu Content konvertieren
  const blocksToContent = () => {
    return blocks.map(block => {
      switch (block.type) {
        case 'heading': return `# ${block.content}`;
        case 'list': return block.content.startsWith('- ') ? block.content : `- ${block.content}`;
        case 'image': return `![Bild](${block.content})`;
        default: return block.content;
      }
    }).join('\n\n');
  };

  // Neuen Block hinzufügen
  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: ''
    };
    setBlocks([...blocks, newBlock]);
  };

  // Block aktualisieren
  const updateBlock = (id, content) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, content } : b));
  };

  // Block löschen
  const deleteBlock = (id) => {
    setBlocks(blocks.filter(b => b.id !== id));
  };

  // Positiv-Vorschau-Modus
  const [showPositivizeDialog, setShowPositivizeDialog] = useState(false);
  const [positivizeFindings, setPositivizeFindings] = useState([]);
  const [positivizeAccepted, setPositivizeAccepted] = useState({});

  // „Text positiv überarbeiten" — öffnet eine Vorschau mit allen geplanten
  // Änderungen; der Autor kann gezielt einzelne übernehmen oder zurückweisen.
  const positivizeAllContent = () => {
    const findings = [];
    // Titel
    const t = positivizeString(postTitle);
    if (t.changes > 0) {
      findings.push({
        key: '__title',
        label: 'Titel',
        original: postTitle,
        suggestion: t.text,
        changes: t.changes,
      });
    }
    // Excerpt
    const e = positivizeString(postExcerpt);
    if (e.changes > 0) {
      findings.push({
        key: '__excerpt',
        label: 'Kurzbeschreibung',
        original: postExcerpt,
        suggestion: e.text,
        changes: e.changes,
      });
    }
    // Blöcke
    const blockFindings = scanForNegativity(blocks);
    blockFindings.forEach((f, i) => {
      findings.push({
        key: f.block_id,
        label: `Block ${i + 1} (${f.type === 'heading' ? 'Überschrift' : f.type === 'list' ? 'Aufzählung' : 'Textabschnitt'})`,
        original: f.original,
        suggestion: f.suggestion,
        changes: f.changes,
      });
    });

    if (findings.length === 0) {
      toast.success('Dein Text klingt bereits durchgehend positiv ✨');
      return;
    }

    // Default: alle Vorschläge sind akzeptiert
    const accepted = {};
    findings.forEach((f) => { accepted[f.key] = true; });
    setPositivizeFindings(findings);
    setPositivizeAccepted(accepted);
    setShowPositivizeDialog(true);
  };

  const applyPositivizeChanges = () => {
    let appliedCount = 0;
    let totalReplacements = 0;
    let newBlocks = [...blocks];

    positivizeFindings.forEach((f) => {
      if (!positivizeAccepted[f.key]) return;
      appliedCount += 1;
      totalReplacements += f.changes;
      if (f.key === '__title') {
        setPostTitle(f.suggestion);
      } else if (f.key === '__excerpt') {
        setPostExcerpt(f.suggestion);
      } else {
        newBlocks = newBlocks.map((b) =>
          b.id === f.key ? { ...b, content: f.suggestion } : b
        );
      }
    });
    setBlocks(newBlocks);
    setShowPositivizeDialog(false);
    if (appliedCount === 0) {
      toast.info('Keine Änderungen übernommen');
    } else {
      toast.success(`${appliedCount} ${appliedCount === 1 ? 'Stelle' : 'Stellen'} mit ${totalReplacements} ${totalReplacements === 1 ? 'Umformulierung' : 'Umformulierungen'} angewendet ✨`);
    }
  };

  const toggleAcceptance = (key) => {
    setPositivizeAccepted((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allAccepted = positivizeFindings.length > 0 && positivizeFindings.every((f) => positivizeAccepted[f.key]);
  const setAllAcceptance = (val) => {
    const next = {};
    positivizeFindings.forEach((f) => { next[f.key] = val; });
    setPositivizeAccepted(next);
  };

  // Block nach oben/unten verschieben
  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setBlocks(newBlocks);
  };

  // Beitrag zum Bearbeiten öffnen
  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setPostTitle(post.title || '');
      setPostExcerpt(post.excerpt || '');
      setPostImage(post.image || '');
      setPostCategory(post.category || '');
      setPostTags((post.tags || []).join(', '));
      setIsPublished(post.is_published || false);
      setBlocks(contentToBlocks(post.content));
    } else {
      setEditingPost(null);
      setPostTitle('');
      setPostExcerpt('');
      setPostImage('');
      setPostCategory('');
      setPostTags('');
      setIsPublished(false);
      setBlocks([]);
    }
    setIsEditing(true);
  };

  // Editor schließen
  const closeEditor = () => {
    setIsEditing(false);
    setEditingPost(null);
  };

  // Beitrag speichern
  const handleSave = async () => {
    if (!postTitle.trim()) {
      toast.error('Bitte einen Titel eingeben');
      return;
    }
    if (blocks.length === 0) {
      toast.error('Bitte mindestens einen Baustein hinzufügen');
      return;
    }

    const postData = {
      title: postTitle,
      content: blocksToContent(),
      excerpt: postExcerpt,
      image: postImage,
      category: postCategory,
      tags: postTags.split(',').map(t => t.trim()).filter(t => t),
      is_published: isPublished
    };

    try {
      if (editingPost) {
        await axiosInstance.put(`/blog/admin/posts/${editingPost.id}`, postData);
        toast.success('Beitrag aktualisiert!');
      } else {
        await axiosInstance.post('/blog/admin/posts', postData);
        toast.success('Beitrag erstellt!');
      }
      closeEditor();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Beitrag löschen
  const handleDelete = async (id) => {
    if (!window.confirm('Beitrag wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/blog/admin/posts/${id}`);
      toast.success('Beitrag gelöscht');
      fetchPosts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Veröffentlichung umschalten
  const togglePublish = async (post) => {
    try {
      await axiosInstance.put(`/blog/admin/posts/${post.id}`, {
        ...post,
        is_published: !post.is_published
      });
      toast.success(post.is_published ? 'Beitrag als Entwurf gespeichert' : 'Beitrag veröffentlicht');
      fetchPosts();
    } catch (error) {
      toast.error('Fehler beim Umschalten');
    }
  };

  // PDF Vorschau öffnen
  const openPdfPreview = async (post) => {
    try {
      toast.loading('PDF wird generiert…', { id: 'pdfgen' });
      const res = await axiosInstance.get(`/blog/admin/posts/${post.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreviewUrl(url);
      setPdfPreviewTitle(post.title || 'Blog-Beitrag');
      setShowPdfDialog(true);
      toast.dismiss('pdfgen');
    } catch (e) {
      toast.dismiss('pdfgen');
      toast.error('Fehler beim Erstellen der PDF');
    }
  };

  const downloadPdf = async (post) => {
    try {
      const res = await axiosInstance.get(`/blog/admin/posts/${post.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (post.title || 'blog').replace(/[^A-Za-z0-9\-]+/g, '_').slice(0, 60);
      link.download = `${safe}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    }
  };

  // Instagram-Teaser
  const openIgTeaser = (post) => {
    setIgPost(post);
    setShowIgDialog(true);
    setCopiedCaption(false);
  };

  const igCaption = (post) => {
    if (!post) return '';
    const cleanExcerpt = (post.excerpt || '').replace(/<[^>]+>/g, '').trim();
    const tags = (post.tags || []).map(t => `#${t.replace(/\s+/g, '')}`).join(' ');
    const baseTags = '#homeyoursoul #handmade #lifestyle #blog';
    const shareUrl = `${window.location.origin}/blog/${post.id}`;
    return [
      `✨ ${post.title}`,
      '',
      cleanExcerpt ? cleanExcerpt.slice(0, 220) : '',
      '',
      `Ganzen Beitrag lesen 👉 ${shareUrl}`,
      '',
      `${baseTags} ${tags}`.trim(),
    ].filter(Boolean).join('\n');
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(igCaption(igPost));
      setCopiedCaption(true);
      toast.success('Caption kopiert');
      setTimeout(() => setCopiedCaption(false), 2500);
    } catch (e) {
      toast.error('Konnte Text nicht kopieren');
    }
  };

  // Download Instagram-ready PNG for a specific format
  const downloadIgImage = async (post, fmt) => {
    try {
      toast.loading('Bild wird erstellt…', { id: `ig-${fmt}` });
      const res = await axiosInstance.get(
        `/blog/admin/posts/${post.id}/instagram/${fmt}.png`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (post.title || 'blog').replace(/[^A-Za-z0-9\-]+/g, '_').slice(0, 60);
      link.download = `${safe}_${fmt}.png`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.dismiss(`ig-${fmt}`);
      toast.success('Bild heruntergeladen');
    } catch (e) {
      toast.dismiss(`ig-${fmt}`);
      toast.error('Download fehlgeschlagen');
    }
  };

  // Gefilterte Beiträge
  const filteredPosts = posts.filter(post =>
    post.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Block-Komponente rendern
  const renderBlock = (block, index) => {
    const blockType = BLOCK_TYPES.find(b => b.type === block.type);
    const Icon = blockType?.icon || AlignLeft;
    
    return (
      <div 
        key={block.id} 
        className="bg-white border rounded-none mb-3"
        style={{ borderColor: '#C4A084' }}
      >
        {/* Block Header */}
        <div 
          className="p-2 flex items-center justify-between"
          style={{ backgroundColor: blockType?.color || '#F5EDE4' }}
        >
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-[#8B7355] cursor-move" />
            <Icon className="h-4 w-4" style={{ color: blockType?.textColor || '#8B7355' }} />
            <span className="text-sm font-medium" style={{ color: blockType?.textColor || '#5D4E3C' }}>
              {blockType?.label || 'Block'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveBlock(index, -1)}
              disabled={index === 0}
              className="h-7 w-7 p-0"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveBlock(index, 1)}
              disabled={index === blocks.length - 1}
              className="h-7 w-7 p-0"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteBlock(block.id)}
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Block Content */}
        <div className="p-3">
          {block.type === 'heading' && (
            <Input
              value={block.content}
              onChange={(e) => updateBlock(block.id, e.target.value)}
              placeholder="Überschrift eingeben…"
              className="text-lg font-bold border-[#C4A084]"
            />
          )}
          {block.type === 'paragraph' && (
            <>
              <Textarea
                value={stripHtmlForEdit(block.content)}
                onChange={(e) => updateBlock(block.id, e.target.value)}
                placeholder="Hier deinen Text eingeben — ganz normal schreiben, ohne irgendwelche Zeichen oder Symbole. Absätze entstehen automatisch beim Drücken der Enter-Taste."
                rows={6}
                className="border-[#C4A084] text-sm leading-relaxed"
              />
              <p className="text-[11px] text-[#8B7355] mt-1.5 italic">
                💡 Du musst nichts formatieren — schreib einfach drauflos, wir machen den Rest.
              </p>
            </>
          )}
          {block.type === 'image' && (
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#5D4E3C] text-white text-sm cursor-pointer hover:bg-[#4A3E30] transition-colors whitespace-nowrap">
                  <ImageIcon className="h-4 w-4" />
                  Bild vom Gerät hochladen
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        toast.info('Bild wird hochgeladen…');
                        const r = await axiosInstance.post('/uploads/upload', fd, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                        });
                        const url = r.data?.url;
                        if (url) {
                          updateBlock(block.id, url);
                          toast.success('Bild hochgeladen');
                        }
                      } catch (err) {
                        toast.error('Hochladen fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
                <Input
                  value={block.content}
                  onChange={(e) => updateBlock(block.id, e.target.value)}
                  placeholder="…oder Bild-URL hier einfügen"
                  className="border-[#C4A084] flex-1"
                />
              </div>
              {block.content && (
                <div className="mt-2 p-2 bg-[#FAF5EE] border border-[#C4A084]">
                  <p className="text-[10px] uppercase tracking-wider text-[#8B7355] mb-1">Vorschau</p>
                  <img
                    src={block.content.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${block.content}` : block.content}
                    alt="Vorschau"
                    className="max-h-48 object-contain border border-[#C4A084]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          )}
          {block.type === 'list' && (() => {
            // Deterministic conversion content (string) → items (array of plain text)
            // and back. Empty content → one empty input. Lines keep their explicit
            // count so adding a new empty Punkt always shows one more input.
            const parseItems = (raw) => {
              if (!raw) return [''];
              return raw.split('\n').map(l => l.replace(/^- ?/, ''));
            };
            const serializeItems = (items) => items.map(l => `- ${l}`).join('\n');
            const items = parseItems(block.content);

            return (
              <div className="space-y-2">
                <p className="text-xs text-[#8B7355] mb-1">
                  📝 Trag pro Zeile einen Punkt ein — die werden automatisch als Liste angezeigt:
                </p>
                {items.map((line, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[#AF9774] font-bold">•</span>
                    <Input
                      value={line}
                      onChange={(e) => {
                        const next = [...items];
                        next[i] = e.target.value;
                        updateBlock(block.id, serializeItems(next));
                      }}
                      placeholder={`Punkt ${i + 1} eingeben…`}
                      className="border-[#C4A084] text-sm"
                      data-testid={`list-item-input-${i}`}
                    />
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = items.filter((_, idx) => idx !== i);
                          updateBlock(block.id, serializeItems(next.length ? next : ['']));
                        }}
                        className="text-[#9C8C7A] hover:text-red-600 text-xl px-2 leading-none"
                        aria-label="Punkt entfernen"
                        data-testid={`list-item-remove-${i}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    updateBlock(block.id, serializeItems([...items, '']));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#F5EDE4] border border-[#C4A084] text-[#5D4E3C] hover:bg-[#EBDFD0]"
                  data-testid="list-add-punkt-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Punkt hinzufügen
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // EDITOR ANSICHT
  if (isEditing) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5EDE4' }}>
        {/* Editor Header */}
        <div className="sticky top-0 z-10 p-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={closeEditor}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <span className="text-white font-semibold">
              {editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={positivizeAllContent}
              variant="outline"
              className="bg-white/15 border-white/40 text-white hover:bg-white/30 hover:text-white"
              data-testid="blog-positivize-btn"
              title="Negative Formulierungen automatisch in positive Sprache überarbeiten"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Positiv überarbeiten
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-white text-[#C4A084] hover:bg-white/90"
            >
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </div>
        </div>

        <div className="p-4 max-w-4xl mx-auto">
          {/* Schreib-Stil-Guide */}
          <div
            className="mb-4 p-3 border-l-4 border-[#C4A084] bg-[#FAF5EE] text-[13px] text-[#5D4E3C] leading-relaxed flex items-start gap-2"
            data-testid="blog-style-guide"
          >
            <span className="text-base leading-none mt-0.5">💡</span>
            <div>
              <strong>Schreib-Stil-Tipp:</strong> Bleib positiv und einladend.
              Statt „Was du nicht tun solltest" lieber „Worauf du achten kannst".
              Formuliere als Einladung, nicht als Verbot — unsere Leserinnen
              entscheiden selbst, was zu ihnen passt. Vermeide Warnungen
              („killen", „falsch", „Stress-Auslöser") und nutze stattdessen
              warme, ermutigende Worte.
            </div>
          </div>

          {/* Grundeinstellungen */}
          <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8F0]">
              <FileText className="h-4 w-4 text-[#E89860]" />
            </div>
            <h2 className="text-base font-semibold text-white">Grundeinstellungen</h2>
          </div>
          
          <Card className="mb-6 border-[#C4A084]">
            <CardContent className="p-4 space-y-4">
              <div>
                <Label className="text-[#5D4E3C] font-semibold">Titel *</Label>
                <Input
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  placeholder="Der Titel deines Beitrags..."
                  className="text-lg border-[#C4A084]"
                />
              </div>
              
              <div>
                <Label className="text-[#5D4E3C] font-semibold">Kurzbeschreibung</Label>
                <Textarea
                  value={postExcerpt}
                  onChange={(e) => setPostExcerpt(e.target.value)}
                  placeholder="Eine kurze Zusammenfassung für die Vorschau..."
                  rows={2}
                  className="border-[#C4A084]"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#5D4E3C] font-semibold">Titelbild URL</Label>
                  <Input
                    value={postImage}
                    onChange={(e) => setPostImage(e.target.value)}
                    placeholder="https://..."
                    className="border-[#C4A084]"
                  />
                </div>
                <div>
                  <Label className="text-[#5D4E3C] font-semibold">Kategorie</Label>
                  <Input
                    value={postCategory}
                    onChange={(e) => setPostCategory(e.target.value)}
                    placeholder="z.B. Tipps, News..."
                    className="border-[#C4A084]"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#5D4E3C] font-semibold">Tags (mit Komma trennen)</Label>
                <Input
                  value={postTags}
                  onChange={(e) => setPostTags(e.target.value)}
                  placeholder="wellness, natur, pflege..."
                  className="border-[#C4A084]"
                />
              </div>
              
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={isPublished}
                  onCheckedChange={setIsPublished}
                />
                <Label className="text-[#5D4E3C]">
                  {isPublished ? 'Wird veröffentlicht' : 'Als Entwurf speichern'}
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Bausteine hinzufügen */}
          <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
              <Plus className="h-4 w-4 text-[#60B060]" />
            </div>
            <h2 className="text-base font-semibold text-white">Baustein hinzufügen</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
            {BLOCK_TYPES.map((blockType) => {
              const Icon = blockType.icon;
              return (
                <Button
                  key={blockType.type}
                  variant="outline"
                  onClick={() => addBlock(blockType.type)}
                  className="h-16 flex flex-col items-center justify-center gap-1 border-[#C4A084]"
                  style={{ backgroundColor: blockType.color }}
                >
                  <Icon className="h-5 w-5" style={{ color: blockType.textColor }} />
                  <span className="text-xs" style={{ color: blockType.textColor }}>{blockType.label}</span>
                </Button>
              );
            })}
          </div>

          {/* Inhalt */}
          <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
              <AlignLeft className="h-4 w-4 text-[#60A0D0]" />
            </div>
            <h2 className="text-base font-semibold text-white">Inhalt ({blocks.length} Bausteine)</h2>
          </div>
          
          {blocks.length === 0 ? (
            <Card className="border-[#C4A084] border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded flex items-center justify-center bg-[#F0F8FF]">
                  <Plus className="h-8 w-8 text-[#60A0D0]" />
                </div>
                <p className="text-[#8B7355] mb-2">Noch keine Bausteine</p>
                <p className="text-sm text-[#A09080]">Klicke oben auf einen Baustein um loszulegen</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {blocks.map((block, index) => renderBlock(block, index))}
            </div>
          )}
        </div>

        {/* Positiv-Vorschau-Dialog */}
        <Dialog open={showPositivizeDialog} onOpenChange={setShowPositivizeDialog}>
          <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#5D4E3C] flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#C4A084]" />
                Positiv-Vorschau · {positivizeFindings.length} {positivizeFindings.length === 1 ? 'Vorschlag' : 'Vorschläge'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <p className="text-[#6D5A47]">
                  Wähle, welche Vorschläge übernommen werden sollen. Hake einzelne Stellen
                  ab, die im Kontext absichtlich so formuliert sind.
                </p>
                <button
                  type="button"
                  onClick={() => setAllAcceptance(!allAccepted)}
                  className="text-xs text-[#8B6F4D] underline whitespace-nowrap ml-3 hover:text-[#5D4E3C]"
                  data-testid="positivize-toggle-all"
                >
                  {allAccepted ? 'Alle abwählen' : 'Alle auswählen'}
                </button>
              </div>

              {positivizeFindings.map((f) => (
                <div
                  key={f.key}
                  className="border border-[#E8DCC8] bg-white"
                  data-testid={`positivize-finding-${f.key}`}
                >
                  <label className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer bg-[#FAF5EE]">
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!positivizeAccepted[f.key]}
                        onChange={() => toggleAcceptance(f.key)}
                        className="h-4 w-4 accent-[#C4A084]"
                        data-testid={`positivize-checkbox-${f.key}`}
                      />
                      <span className="text-sm font-semibold text-[#5D4E3C]">{f.label}</span>
                    </span>
                    <span className="text-[11px] text-[#8B7355]">
                      {f.changes} {f.changes === 1 ? 'Änderung' : 'Änderungen'}
                    </span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-[#E8DCC8]">
                    <div className="p-3 bg-[#FFF6F4]">
                      <div className="text-[10px] tracking-widest text-[#B0512E] mb-1">ORIGINAL</div>
                      <p className="text-sm text-[#5D4E3C] leading-relaxed whitespace-pre-wrap">
                        {f.original}
                      </p>
                    </div>
                    <div className="p-3 bg-[#F0F8F0]">
                      <div className="text-[10px] tracking-widest text-[#3F7A3F] mb-1">VORSCHLAG</div>
                      <p className="text-sm text-[#5D4E3C] leading-relaxed whitespace-pre-wrap">
                        {f.suggestion}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E8DCC8] mt-2">
              <Button
                variant="outline"
                onClick={() => setShowPositivizeDialog(false)}
                className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
                data-testid="positivize-cancel"
              >
                Abbrechen
              </Button>
              <Button
                onClick={applyPositivizeChanges}
                className="bg-[#C4A084] hover:bg-[#B89968] text-white"
                disabled={positivizeFindings.filter((f) => positivizeAccepted[f.key]).length === 0}
                data-testid="positivize-apply"
              >
                <Check className="h-4 w-4 mr-2" />
                {positivizeFindings.filter((f) => positivizeAccepted[f.key]).length} Übernehmen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // LISTE ANSICHT
  if (loading) {
    return <div className="p-8">Lade Beiträge...</div>;
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ backgroundColor: '#F5EDE4' }}>
      {/* Goldbraune Haupt-Überschrift */}
      <div className="mb-4 p-3 md:p-4 flex items-center gap-3" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
          <BookOpen className="h-5 w-5" style={{ color: '#C4A084' }} />
        </div>
        <div>
          <h1 className="text-lg md:text-xl font-bold text-white">Blog verwalten</h1>
          <p className="text-xs md:text-sm text-white/80">{posts.length} Beiträge</p>
        </div>
      </div>

      {/* Aktionen */}
      <div className="mb-3 p-2 px-3 flex items-center justify-between flex-wrap gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
            <Plus className="h-4 w-4 text-[#60B060]" />
          </div>
          <h2 className="text-base font-semibold text-white">Aktionen</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {posts.length === 0 && (
            <Button
              onClick={async () => {
                if (!window.confirm('11 Standard-Beiträge wiederherstellen?\n(Bestehende werden NICHT überschrieben.)')) return;
                try {
                  const r = await axiosInstance.post('/blog/admin/seed-from-preview');
                  const { inserted, skipped_existing, total_in_db } = r.data;
                  if (inserted > 0) {
                    toast.success(`${inserted} Beiträge wiederhergestellt! Aktuell ${total_in_db} im Blog.`);
                  } else if (skipped_existing > 0) {
                    toast.info(`Alle ${skipped_existing} Beiträge waren bereits vorhanden. Aktuell ${total_in_db} im Blog.`);
                  } else {
                    toast.warning('Keine Beiträge importiert. Bitte Admin kontaktieren.');
                  }
                  loadPosts();
                } catch (e) {
                  const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
                  toast.error(`Wiederherstellen fehlgeschlagen: ${msg}`, { duration: 8000 });
                }
              }}
              variant="outline"
              className="bg-white/20 text-white border-white hover:bg-white hover:text-[#C4A084]"
              data-testid="restore-blog-posts-btn"
            >
              📚 Standardbeiträge wiederherstellen
            </Button>
          )}
          <Button
            onClick={() => openEditor()}
            className="bg-white text-[#C4A084] hover:bg-white/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neuer Beitrag
          </Button>
        </div>
      </div>

      {/* Suche */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#8B7355] pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Beiträge suchen..."
            className="border-[#C4A084]"
            style={{ paddingLeft: '2.75rem' }}
          />
        </div>
      </div>

      {/* Analytics-Widget (Admin-privat) — Views & Meist gelesener Beitrag */}
      {(() => {
        const publishedPosts = posts.filter(p => p.is_published);
        const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
        if (totalViews === 0 && publishedPosts.length === 0) return null;
        const topByViews = [...posts]
          .filter(p => (p.views || 0) > 0)
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 3);
        const topOne = topByViews[0];
        const avgViews = publishedPosts.length ? Math.round(totalViews / publishedPosts.length) : 0;
        const totalLikesForRate = posts.reduce((sum, p) => sum + (p.likes || 0), 0);
        const engagementRate = totalViews > 0 ? Math.round((totalLikesForRate / totalViews) * 1000) / 10 : 0;
        return (
          <div
            className="mb-4 border border-[#E8DCC8] bg-white p-4"
            data-testid="blog-analytics-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#F0F8FF]">
                <BookOpen className="h-4 w-4 text-[#60A0D0]" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-[#5D4E3C]">Blog-Analytics <span className="text-[10px] font-normal text-[#B0855C] italic ml-1">· nur für dich sichtbar</span></h3>
                <p className="text-[11px] text-[#8B7355]">
                  {totalViews} Aufrufe insgesamt · Ø {avgViews} pro Beitrag · {engagementRate}% Engagement (Likes/Views)
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Meist gelesener Beitrag (Hero-Card) */}
              {topOne ? (
                <button
                  type="button"
                  onClick={() => openEditor(topOne)}
                  className="md:col-span-2 text-left bg-gradient-to-br from-[#F0F8FF] to-[#E8F0F8] p-4 border border-[#D5E5F0] hover:border-[#60A0D0] hover:shadow-md transition-all cursor-pointer group"
                  title="Klicken zum Bearbeiten"
                  data-testid="blog-analytics-top-views"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-14 h-14 bg-white border border-[#D5E5F0] flex items-center justify-center">
                      <Eye className="h-6 w-6 text-[#60A0D0]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] tracking-widest uppercase text-[#60A0D0] font-medium mb-1">
                        📖 Meist gelesener Beitrag
                      </p>
                      <h4 className="text-base font-semibold text-[#5D4E3C] group-hover:underline line-clamp-2 leading-tight">
                        {topOne.title}
                      </h4>
                      <div className="mt-1 flex items-center gap-3 text-xs text-[#8B7355]">
                        <span className="inline-flex items-center gap-1">
                          <Eye className="h-3 w-3" /> <strong className="text-[#5D4E3C]">{topOne.views || 0}</strong> Aufrufe
                        </span>
                        {(topOne.likes || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-[#B0512E]">
                            <Heart className="h-3 w-3" fill="#E27158" strokeWidth={0} /> {topOne.likes}
                          </span>
                        )}
                        {topOne.category && (
                          <span className="text-[10px] tracking-wider uppercase text-[#8B7355]">
                            {topOne.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="md:col-span-2 bg-[#FAF5EE] p-4 border border-dashed border-[#E8DCC8] text-center text-xs text-[#8B7355]">
                  Noch keine Aufrufe registriert. Sobald Leser deine Beiträge öffnen, siehst du hier deinen Bestseller.
                </div>
              )}

              {/* Übrige Top-2 als kompakte Cards */}
              <div className="space-y-2">
                {topByViews.slice(1, 3).map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openEditor(p)}
                    className="w-full text-left bg-white p-2.5 border border-[#E8DCC8] hover:border-[#60A0D0] transition-all cursor-pointer group"
                    title="Klicken zum Bearbeiten"
                    data-testid={`blog-analytics-runner-${idx + 2}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#8B7355] flex-shrink-0">#{idx + 2}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-[#5D4E3C] group-hover:underline truncate">{p.title}</p>
                        <p className="text-[10px] text-[#8B7355] inline-flex items-center gap-1">
                          <Eye className="h-2.5 w-2.5" /> {p.views || 0} Aufrufe
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
                {topByViews.length < 3 && (
                  <div className="bg-[#FAF5EE] p-2 border border-dashed border-[#E8DCC8] text-center text-[10px] text-[#8B7355]">
                    Mehr Beiträge veröffentlichen für Vergleich
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Top-Beliebt-Widget */}
      {(() => {
        const topPosts = [...posts]
          .filter(p => (p.likes || 0) > 0)
          .sort((a, b) => (b.likes || 0) - (a.likes || 0))
          .slice(0, 3);
        const totalLikes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);
        if (totalLikes === 0) return null;
        return (
          <div
            className="mb-6 border border-[#E8DCC8] bg-gradient-to-br from-[#FAF5EE] to-[#F5EDE4] p-4"
            data-testid="blog-top-liked-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-white">
                <TrendingUp className="h-4 w-4" style={{ color: '#E27158' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#5D4E3C]">Was deine Leser lieben</h3>
                <p className="text-[11px] text-[#8B7355]">
                  Insgesamt {totalLikes} {totalLikes === 1 ? 'Like' : 'Likes'} · Top {topPosts.length} Beiträge
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {topPosts.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openEditor(p)}
                  className="text-left bg-white p-3 border border-[#E8DCC8] hover:border-[#C4A084] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
                  title="Klicken zum Bearbeiten"
                  data-testid={`blog-top-liked-${idx + 1}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span
                      className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full"
                      style={{
                        background: idx === 0 ? '#FBE4DA' : '#F5EDE4',
                        color: idx === 0 ? '#B0512E' : '#8B7355',
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-[#B0512E] font-medium">
                      <Heart className="h-3.5 w-3.5" fill="#E27158" strokeWidth={0} />
                      {p.likes || 0}
                    </span>
                  </div>
                  <h4 className="text-sm font-medium text-[#5D4E3C] line-clamp-2 leading-tight group-hover:underline">
                    {p.title}
                  </h4>
                  {p.category && (
                    <p className="text-[10px] tracking-wider text-[#8B7355] mt-1 uppercase">
                      {p.category}
                    </p>
                  )}
                  <p className="text-[10px] text-[#B0855C] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    ✏️ Klicken zum Bearbeiten
                  </p>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Beiträge Liste */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFFEF5]">
          <FileText className="h-4 w-4 text-[#D4B030]" />
        </div>
        <h2 className="text-base font-semibold text-white flex-1">Alle Beiträge</h2>
        <span className="text-[11px] text-white/80 italic hidden sm:inline">
          Tipp: Karte klicken zum Bearbeiten
        </span>
      </div>

      {filteredPosts.length === 0 ? (
        <Card className="border-[#C4A084]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded flex items-center justify-center bg-[#F8F0FF]">
              <BookOpen className="h-8 w-8 text-[#A070D0]" />
            </div>
            <p className="text-[#8B7355]">Noch keine Beiträge vorhanden</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map((post) => (
            <Card 
              key={post.id} 
              className={`border-[#C4A084] ${!post.is_published ? 'opacity-70' : ''} hover:shadow-md hover:border-[#5D4E3C] transition-all cursor-pointer group`}
              data-testid={`blog-card-${post.id}`}
              onClick={(e) => {
                // Klick außerhalb der Buttons öffnet den Editor
                if (e.target.closest('button')) return;
                openEditor(post);
              }}
            >
              <CardHeader className="p-0">
                <div 
                  className="p-3 flex items-center justify-between"
                  style={{ backgroundColor: post.is_published ? '#C4A084' : '#E8DFD4' }}
                >
                  <CardTitle
                    className="text-white text-sm truncate flex-1 group-hover:underline"
                    title="Klicken zum Bearbeiten"
                  >
                    {post.title}
                  </CardTitle>
                  <div className="flex gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); togglePublish(post); }}
                      className="h-7 w-7 p-0 text-white hover:bg-white/20"
                      title={post.is_published ? 'Als Entwurf' : 'Veröffentlichen'}
                    >
                      {post.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {post.image && (
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-32 object-cover mb-3 rounded border border-[#E8DFD4]"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                {post.excerpt && (
                  <p className="text-sm text-[#8B7355] mb-3 line-clamp-2">{post.excerpt}</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded ${post.is_published ? 'bg-[#F0FFF0] text-[#60B060]' : 'bg-[#FFF8F0] text-[#E89860]'}`}>
                      {post.is_published ? 'Veröffentlicht' : 'Entwurf'}
                    </span>
                    {(post.likes || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[#FBE4DA] text-[#B0512E]"
                        title={`${post.likes} ${post.likes === 1 ? 'Like' : 'Likes'} von Lesern`}
                        data-testid={`blog-card-likes-${post.id}`}
                      >
                        <Heart className="h-3 w-3" fill="#E27158" strokeWidth={0} />
                        {post.likes}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openPdfPreview(post); }}
                      className="h-8 w-8 p-0 bg-[#FFF8F0] hover:bg-[#FFEFD9] text-[#E89860]"
                      title="PDF-Vorschau"
                      data-testid={`blog-pdf-preview-${post.id}`}
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openIgTeaser(post); }}
                      className="h-8 w-8 p-0 bg-[#F8F0FF] hover:bg-[#EFE0FF] text-[#A070D0]"
                      title="Instagram-Teaser"
                      data-testid={`blog-ig-teaser-${post.id}`}
                    >
                      <Instagram className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openEditor(post); }}
                      className="h-8 px-2 bg-[#F0F8FF] hover:bg-[#E0F0FF] text-[#60A0D0] text-xs font-medium"
                      title="Bearbeiten"
                      data-testid={`blog-edit-${post.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Bearbeiten
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}
                      className="h-8 w-8 p-0 bg-[#FFF0F0] hover:bg-[#FFE0E0] text-[#D06060]"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* PDF Vorschau Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={(o) => { setShowPdfDialog(o); if (!o && pdfPreviewUrl) { window.URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="text-[#5D4E3C]">PDF-Vorschau · {pdfPreviewTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full">
            {pdfPreviewUrl && (
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-[calc(90vh-80px)]"
                title="Blog PDF Vorschau"
                data-testid="blog-pdf-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Instagram-Teaser Dialog */}
      <Dialog open={showIgDialog} onOpenChange={setShowIgDialog}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C] flex items-center gap-2">
              <Instagram className="h-5 w-5 text-[#A070D0]" />
              Instagram-Teaser · {igPost?.title}
            </DialogTitle>
          </DialogHeader>
          {igPost && (
            <div className="space-y-6">
              <p className="text-sm text-[#6D5A47]">
                Lade dein Wunsch-Format direkt als fertige PNG-Datei herunter und
                poste es bei Instagram. Caption kannst du unten kopieren.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* 1:1 Feed */}
                <div className="flex flex-col items-center gap-2" data-testid="ig-format-square">
                  <span className="text-xs tracking-widest text-[#8B7355]">FEED · 1:1</span>
                  <IgCard post={igPost} ratio="square" />
                  <Button
                    onClick={() => downloadIgImage(igPost, 'square')}
                    size="sm"
                    variant="outline"
                    className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE] mt-2"
                    data-testid="ig-download-square"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    PNG 1080×1080
                  </Button>
                </div>
                {/* 4:5 Portrait */}
                <div className="flex flex-col items-center gap-2" data-testid="ig-format-portrait">
                  <span className="text-xs tracking-widest text-[#8B7355]">PORTRAIT · 4:5</span>
                  <IgCard post={igPost} ratio="portrait" />
                  <Button
                    onClick={() => downloadIgImage(igPost, 'portrait')}
                    size="sm"
                    variant="outline"
                    className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE] mt-2"
                    data-testid="ig-download-portrait"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    PNG 1080×1350
                  </Button>
                </div>
                {/* 9:16 Story */}
                <div className="flex flex-col items-center gap-2" data-testid="ig-format-story">
                  <span className="text-xs tracking-widest text-[#8B7355]">STORY / REELS · 9:16</span>
                  <IgCard post={igPost} ratio="story" />
                  <Button
                    onClick={() => downloadIgImage(igPost, 'story')}
                    size="sm"
                    variant="outline"
                    className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE] mt-2"
                    data-testid="ig-download-story"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    PNG 1080×1920
                  </Button>
                </div>
              </div>

              <div className="border-t border-[#E8DCC8] pt-4">
                <Label className="text-[#5D4E3C] font-semibold">Caption</Label>
                <Textarea
                  readOnly
                  value={igCaption(igPost)}
                  rows={8}
                  className="mt-2 font-mono text-xs border-[#C4A084]"
                  data-testid="ig-caption-text"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={copyCaption}
                    className="bg-[#C4A084] hover:bg-[#B89968] text-white"
                    data-testid="ig-copy-caption"
                  >
                    {copiedCaption ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copiedCaption ? 'Kopiert' : 'Caption kopieren'}
                  </Button>
                  <Button
                    onClick={() => downloadPdf(igPost)}
                    variant="outline"
                    className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#FAF5EE]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Blog als PDF
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;

/**
 * IgCard — Instagram-fertige Mini-Karte mit Brand-Design.
 * Wird genutzt, um vom Admin per Screenshot direkt geteilt zu werden.
 */
const IgCard = ({ post, ratio }) => {
  // Display sizes (small previews fitting into the dialog)
  const sizes = {
    square: { w: 230, h: 230 },
    portrait: { w: 230, h: 287 },
    story: { w: 200, h: 355 },
  };
  const { w, h } = sizes[ratio] || sizes.square;
  const hero = post.image || (post.featured_image) || '';
  const cleanExcerpt = (post.excerpt || '').replace(/<[^>]+>/g, '').trim();

  return (
    <div
      className="relative overflow-hidden shadow-md"
      style={{
        width: `${w}px`,
        height: `${h}px`,
        background: hero ? '#000' : 'linear-gradient(135deg, #C4A084 0%, #8B7355 100%)',
      }}
      data-testid={`ig-card-${ratio}`}
    >
      {hero ? (
        <img
          src={hero}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.7 }}
        />
      ) : null}
      {/* Brand corner badge */}
      <div
        className="absolute top-3 left-3 px-2 py-1 text-[9px] tracking-[3px] text-white"
        style={{ background: 'rgba(93,78,60,0.85)' }}
      >
        HOMEYOURSOUL
      </div>
      {/* Bottom gradient + content */}
      <div
        className="absolute inset-0 flex flex-col justify-end p-3"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 80%)',
        }}
      >
        {post.category && (
          <span className="text-[8px] tracking-[2px] text-[#FAE4C8] mb-1 uppercase">
            {post.category}
          </span>
        )}
        <h4
          className="text-white font-semibold leading-tight mb-1"
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: ratio === 'story' ? '14px' : '13px',
          }}
        >
          {(post.title || '').slice(0, 60)}
        </h4>
        {ratio !== 'square' && cleanExcerpt && (
          <p
            className="text-white/85 leading-snug"
            style={{ fontSize: '9px' }}
          >
            {cleanExcerpt.slice(0, ratio === 'story' ? 140 : 90)}
            {cleanExcerpt.length > (ratio === 'story' ? 140 : 90) ? '…' : ''}
          </p>
        )}
        <div className="mt-2 inline-block self-start px-2 py-1 text-[9px] tracking-widest bg-[#C4A084] text-white">
          WEITERLESEN →
        </div>
      </div>
    </div>
  );
};
